/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getFocusableDescendants, findAndFocusTabbableItemOutsideDOM } from "utils/AccessibilityUtils";
import { getPagesViewElementHandle } from "selectors/pagedViewState";
import { getPagesInDOM, getTotalPages } from "selectors/documentSelectors";
import { resetCaretMode } from "utils/CaretModeUtils";
import { getContextMenuHandle } from "selectors/contextMenuSelectors";

class ContentTabbingManager {
  constructor(stores) {
    this.stores = stores;
  }

  handleForwardTab(tabbableContentInDOM, activeElement) {
    // Check if focus about to move out of pagesView duw to forward tab
    if (
      tabbableContentInDOM.length === 0 ||
      activeElement === tabbableContentInDOM[tabbableContentInDOM.length - 1] ||
      activeElement.compareDocumentPosition(tabbableContentInDOM[tabbableContentInDOM.length - 1]) &
        Node.DOCUMENT_POSITION_PRECEDING
    ) {
      const { end: lastPageIndexInDOM } = getPagesInDOM(this.stores);
      // Search in pages outside DOM for focusable elments
      for (let pageIndex = lastPageIndexInDOM + 1; pageIndex < getTotalPages(this.stores); pageIndex++)
        if (findAndFocusTabbableItemOutsideDOM(this.stores, pageIndex, true)) return true;
    }
    return false;
  }

  handleBackwardTab(tabbableContentInDOM, activeElement) {
    // Check if focus about to move out of pagesView due to backward tab
    if (
      tabbableContentInDOM.length === 0 ||
      activeElement === tabbableContentInDOM[0] ||
      activeElement.compareDocumentPosition(tabbableContentInDOM[0]) & Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      const { start: firstPageIndexInDOM } = getPagesInDOM(this.stores);
      // Search in pages outside DOM for focusable elments
      for (let pageIndex = firstPageIndexInDOM - 1; pageIndex >= 0; pageIndex--)
        if (findAndFocusTabbableItemOutsideDOM(this.stores, pageIndex, false)) return true;
    }
    return false;
  }

  // return true if preventDefault should be called
  handleTab(isShiftKeyPressed) {
    const activeElement = document.activeElement;
    const pagesViewElement = getPagesViewElementHandle(this.stores);
    // Check if focus is already on a tabbable element in PDF content
    if (activeElement && pagesViewElement && pagesViewElement.contains(activeElement)) {
      const tabbableContentInDOM = getFocusableDescendants(pagesViewElement);
      return isShiftKeyPressed
        ? this.handleBackwardTab(tabbableContentInDOM, activeElement)
        : this.handleForwardTab(tabbableContentInDOM, activeElement);
    }
    return false;
  }

  onFocus() {
    const activeElement = document.activeElement;
    const pagesViewElement = getPagesViewElementHandle(this.stores);
    const contextMenuElement = getContextMenuHandle(this.stores);

    const isTextInput = element =>
      element.tagName === "TEXTAREA" ||
      (element.tagName === "INPUT" && element.getAttribute("data-testid") === "textInput");

    if (activeElement && pagesViewElement && pagesViewElement.contains(activeElement)) {
      this.stores.accessibilityStore.setIsActiveElementInPagesView(true);
      if (isTextInput(activeElement)) {
        // Focus is inside the form fields within the PDF document, reset cursor in this case
        resetCaretMode();
      }
    } else {
      this.stores.accessibilityStore.setIsActiveElementInPagesView(false);
      if (activeElement && contextMenuElement && contextMenuElement.contains(activeElement)) {
        this.stores.accessibilityStore.setIsActiveElementInContextMenu(true);
      } else {
        this.stores.accessibilityStore.setIsActiveElementInContextMenu(false);
        resetCaretMode();
      }
    }
  }
}

export { ContentTabbingManager };
